import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
  Paper,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import LockIcon from '@mui/icons-material/Lock';
import SortIcon from '@mui/icons-material/Sort';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

// Tablo sütunu arayüzü
interface TableColumn {
  id: string;
  label: string;
  field: string;
  visible: boolean;
  fixed: boolean;
  sortable?: boolean;
  description?: string;
}

interface AccountsTableSettingsProps {
  open: boolean;
  onClose: () => void;
  onSave: (columns: TableColumn[]) => void;
  currentColumns: TableColumn[];
}

const AccountsTableSettings: React.FC<AccountsTableSettingsProps> = ({
  open,
  onClose,
  onSave,
  currentColumns
}) => {
  const { t } = useTranslation('accounting');
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);

  useEffect(() => {
    // Tabloda güncellemeleri izole etmek için derin kopyalama
    setColumns(JSON.parse(JSON.stringify(currentColumns)));
  }, [currentColumns, open]);

  // Görünürlüğü değiştirme
  const handleToggleVisibility = (id: string) => {
    setColumns(prevColumns =>
      prevColumns.map(column => {
        // Sabit sütunlar her zaman görünür olmalı
        if (column.id === id && !column.fixed) {
          const newVisible = !column.visible;
          return { 
            ...column, 
            visible: newVisible,
            // Görünürlük kapatılırsa sıralama da kapatılsın
            ...(newVisible ? {} : { sortable: false })
          };
        }
        return column;
      })
    );
  };

  // Sıralanabilirliği değiştirme
  const handleToggleSortable = (id: string) => {
    const nonSortableIds = ['actions', 'rowNumber'];
    setColumns(prevColumns =>
      prevColumns.map(column => {
        if (column.id === id && !nonSortableIds.includes(column.id)) {
          return { ...column, sortable: !column.sortable };
        }
        return column;
      })
    );
  };

  // Sıralama desteklemeyen sütunlar
  const nonSortableIds = ['actions', 'rowNumber'];

  // Değişiklikleri kaydetme
  const handleSave = () => {
    onSave(columns);
    onClose();
  };

  // İptal etme
  const handleCancel = () => {
    onClose();
  };

  // Sütunları gruplama: sabit ve değiştirilebilir olanlar
  const fixedColumns = columns.filter(column => column.fixed);
  const customizableColumns = columns.filter(column => !column.fixed);

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ 
          sx: { 
            borderRadius: 2,
          } 
        }}
      >
        <DialogTitle 
          sx={{ 
            bgcolor: 'primary.main', 
            color: 'white', 
            fontWeight: 'bold',
            py: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>{t('current.modals.tableSettings.title')}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton color="inherit" onClick={() => setInfoDialogOpen(true)} aria-label={t('current.menu.information')}>
              <InfoOutlinedIcon />
            </IconButton>
            <IconButton color="inherit" onClick={onClose} aria-label={t('current.actions.close')}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ py: 3 }}>
          {/* Sabit Sütunlar */}
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <LockIcon sx={{ mr: 1 }} /> {t('current.modals.tableSettings.fixedColumns')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('current.modals.tableSettings.fixedColumnsInfo')}
          </Typography>
          
          <Grid container spacing={2}>
            {fixedColumns.map((column) => (
              <Grid item xs={12} sm={6} md={4} key={column.id}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    bgcolor: 'primary.light',
                    color: 'primary.contrastText'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DragIndicatorIcon sx={{ color: 'primary.contrastText', mr: 1, opacity: 0.7 }} />
                      <Typography variant="body1">{column.label}</Typography>
                    </Box>
                    {column.description && (
                      <Tooltip title={column.description}>
                        <IconButton size="small" sx={{ color: 'primary.contrastText' }}>
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, pl: 3.5 }}>
                    <FormControlLabel
                      control={<Switch checked={true} disabled size="small" />}
                      label={<Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>{t('current.modals.tableSettings.visible')}</Typography>}
                      sx={{ margin: 0 }}
                    />
                    {!nonSortableIds.includes(column.id) && (
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={!!column.sortable}
                            onChange={() => handleToggleSortable(column.id)}
                            size="small"
                            sx={{ 
                              '& .MuiSwitch-switchBase.Mui-checked': { color: 'white' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.5)' },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <SortIcon sx={{ fontSize: 14, color: 'primary.contrastText', opacity: 0.9 }} />
                            <Typography variant="caption" sx={{ color: 'primary.contrastText', opacity: 0.9 }}>{t('current.modals.tableSettings.sort')}</Typography>
                          </Box>
                        }
                        sx={{ margin: 0 }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
        
          {/* Özelleştirilebilir Sütunlar */}
          <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            <DragIndicatorIcon sx={{ mr: 1 }} /> {t('current.modals.tableSettings.customizableColumns')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('current.modals.tableSettings.customizableColumnsInfo')}
          </Typography>
          
          <Grid container spacing={2}>
            {customizableColumns.map((column) => (
              <Grid item xs={12} sm={6} md={4} key={column.id}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid',
                    borderColor: column.visible ? 'primary.light' : 'divider',
                    borderRadius: 1,
                    bgcolor: column.visible ? 'rgba(0, 0, 0, 0.04)' : 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 1, opacity: 0.7 }} />
                      <Typography 
                        variant="body1"
                        sx={{ 
                          color: column.visible ? 'text.primary' : 'text.secondary',
                          fontWeight: column.visible ? 'medium' : 'normal'
                        }}
                      >
                        {column.label}
                      </Typography>
                    </Box>
                    {column.description && (
                      <Tooltip title={column.description}>
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1.5, pl: 3.5 }}>
                    <FormControlLabel
                      control={
                        <Switch 
                          checked={column.visible}
                          onChange={() => handleToggleVisibility(column.id)}
                          size="small"
                        />
                      }
                      label={<Typography variant="caption" color="text.secondary">{t('current.modals.tableSettings.visible')}</Typography>}
                      sx={{ margin: 0 }}
                    />
                    {!nonSortableIds.includes(column.id) && (
                      <FormControlLabel
                        control={
                          <Switch 
                            checked={!!column.sortable}
                            onChange={() => handleToggleSortable(column.id)}
                            size="small"
                            color="secondary"
                            disabled={!column.visible}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <SortIcon sx={{ fontSize: 14, color: column.visible ? 'text.secondary' : 'text.disabled' }} />
                            <Typography variant="caption" color={column.visible ? 'text.secondary' : 'text.disabled'}>{t('current.modals.tableSettings.sort')}</Typography>
                          </Box>
                        }
                        sx={{ margin: 0 }}
                      />
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
          </Paper>
        </DialogContent>
        
        <Divider />
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleCancel}
            variant="outlined"
          >
            {t('current.actions.cancel')}
          </Button>
          <Button 
            onClick={handleSave}
            variant="contained"
            color="primary"
          >
            {t('current.modals.tableSettings.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('current.modals.tableSettings.infoDialog.title', { defaultValue: 'Sütun Yönetimi Bilgilendirme' })}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('current.modals.tableSettings.infoDialog.description', {
              defaultValue:
                'Bu sayfa, cari listesindeki sütun görünürlüğü ve sıralama ayarlarının günlük operasyonu nasıl etkilediğini açıklar. Doğru yapılandırılmış bir tablo; arama, takip, raporlama ve işlem hızını doğrudan iyileştirir.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.tableSettings.infoDialog.fixedColumnsTitle', { defaultValue: 'Sabit Sütunlar' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.tableSettings.infoDialog.fixedColumnsText', {
              defaultValue:
                'Sabit sütunlar, tablonun temel navigasyon ve işlem akışını korumak için sürekli görünür tutulur. Bu alanların görünürlüğü kapatılamaz; çünkü satır kimliği, temel bakiye görünümü ve satır işlemleri bu çekirdek sütunlar üzerinden yürür.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.tableSettings.infoDialog.customColumnsTitle', { defaultValue: 'Özelleştirilebilir Sütunlar' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.tableSettings.infoDialog.customColumnsText', {
              defaultValue:
                'Özelleştirilebilir sütunlar, işletmenin ihtiyaç duyduğu bilgi yoğunluğuna göre açılıp kapatılabilir. Örneğin çağrı/tahsilat odaklı kullanımda iletişim alanlarını öne çıkarabilir, muhasebe kontrolünde vergi ve finans alanlarını görünür tutabilirsiniz.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.tableSettings.infoDialog.sortingTitle', { defaultValue: 'Sıralama Davranışı' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.tableSettings.infoDialog.sortingText', {
              defaultValue:
                'Görünürlüğü kapatılan bir sütunda sıralama da otomatik kapanır. Böylece kullanıcı görünmeyen bir alana göre sıralanmış bir listeyle karşılaşmaz ve liste davranışı her zaman ekranda görülen sütunlarla tutarlı kalır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.tableSettings.infoDialog.recommendedUsageTitle', { defaultValue: 'Önerilen Kullanım Akışı' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t('current.modals.tableSettings.infoDialog.recommendedUsageItem1', {
              defaultValue: '• İlk olarak ekibinizin günlükte en sık kullandığı 5-7 sütunu görünür bırakın.'
            })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {t('current.modals.tableSettings.infoDialog.recommendedUsageItem2', {
              defaultValue: '• Sonra karar destek için gerekli 1-2 kritik sütunda sıralamayı aktif edin.'
            })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.tableSettings.infoDialog.recommendedUsageItem3', {
              defaultValue: '• Kaydetmeden önce listeyi kısa bir kontrol ederek görünmeyen ama sıralı kalan sütun olmadığını doğrulayın.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.tableSettings.infoDialog.teamBenefitTitle', { defaultValue: 'Ekip Verimliliğine Etkisi' })}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            {t('current.modals.tableSettings.infoDialog.teamBenefitText', {
              defaultValue:
                'Standart bir sütun düzeni, farklı kullanıcıların aynı listeyi benzer şekilde yorumlamasını sağlar. Bu da görev devri, hata ayıklama ve müşteri takibinde iletişim maliyetini azaltır.'
            })}
          </Typography>

          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 0.5 }}>
            {t('current.modals.tableSettings.infoDialog.persistenceTitle', { defaultValue: 'Ayarların Kalıcılığı' })}
          </Typography>
          <Typography variant="body2">
            {t('current.modals.tableSettings.infoDialog.persistenceText', {
              defaultValue:
                'Kaydettiğiniz sütun tercihleri cihaz bazlı olarak saklanır ve aynı cihazda modülü tekrar açtığınızda korunur. Farklı cihazda aynı görünümü kullanmak için ayarları o cihazda da bir kez yapılandırmanız gerekir.'
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>
            {t('current.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AccountsTableSettings; 